import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LIBRARY_TILE_STYLE,
  periodLabel,
  portalLibraryDocumentHref,
} from '../../../../lib/portalLibrary/listShapes';
import {
  PORTAL_LIBRARY_SECTION_LABELS,
  type PortalLibraryTile,
} from '../../../../types/portalLibrary';

// CLIENT PORTAL LIBRARY — one tile.
//
// ⚠️⚠️ A NULL THUMBNAIL IS A DESIGNED STATE (brief §5), and the typographic tile
// is built FIRST because it is what stops the grid ever looking broken: the
// title set in Kyck's display face on the section's colour. A thumbnail that
// FAILS to load falls back to the same tile rather than a broken image.
//
// ⚠️⚠️ RULE 12 — NEVER THE CLIENT'S BRAND. The generated tile uses Kyck's own
// type and colour only. No logo, no brand colour, no inference, no substitution.
//
// ⚠️⚠️ The tile opens the PORTAL'S OWN document page (Round 3), inside the
// portal, signed in. That page asks the server — every time — whether this
// viewer may still open this document. It used to open the report's public
// `/r/:token` link in a new tab, which a client kept after they left.

export function LibraryTile({ tile }: { tile: PortalLibraryTile }) {
  const [imageFailed, setImageFailed] = useState(false);
  const style = LIBRARY_TILE_STYLE[tile.section];
  const showImage = tile.thumbnailUrl !== null && !imageFailed;

  return (
    <Link to={portalLibraryDocumentHref(tile.id)} className="plib-tile" aria-label={tile.title}>
      <div className="plib-thumb" data-testid="library-thumb">
        {showImage ? (
          <img
            src={tile.thumbnailUrl!}
            alt=""
            onError={() => setImageFailed(true)}
            className="plib-thumb-img"
          />
        ) : (
          <div
            className="plib-thumb-type"
            data-testid="library-thumb-type"
            style={{ background: style.background, color: style.foreground }}
          >
            {/*
              ⚠️ The kicker is 12px regular — body text, 4.5:1 — so where the
              tile's own pair does not clear that (college's marine), it sits on
              a plate. See LIBRARY_TILE_STYLE.
            */}
            <span
              className="plib-thumb-kicker"
              style={
                style.kickerPlate
                  ? {
                      background: style.kickerPlate.background,
                      color: style.kickerPlate.foreground,
                      alignSelf: 'flex-start',
                      padding: '2px 6px',
                      borderRadius: '4px',
                    }
                  : undefined
              }
            >
              {tile.section === 'strategy.monthly'
                ? periodLabel(tile.period)
                : PORTAL_LIBRARY_SECTION_LABELS[tile.section]}
            </span>
            <span className="plib-thumb-title">{tile.title}</span>
          </div>
        )}
      </div>
      <div className="plib-meta">
        <span className="plib-title">{tile.title}</span>
        {tile.summary && <span className="plib-summary">{tile.summary}</span>}
      </div>
    </Link>
  );
}
